import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  REMOTE_SUPPORT_URL,
  PHONE,
  PHONE_HREF,
  EMAIL,
} from "@/data/content";

const EASE = [0.16, 1, 0.3, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "w-full border border-white/15 bg-transparent px-5 py-4 text-base text-white placeholder:text-white/30 transition-colors duration-300 focus:border-[#0033FF] focus:outline-none";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent — we'll be in touch shortly.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please call us instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contact"
      className="border-t border-white/10 bg-[#0D0D0D]"
      data-testid="contact-section"
    >
      <div className="mx-auto grid max-w-[1440px] gap-16 px-6 py-28 md:px-12 md:py-40 lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
        >
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#0033FF]">
            ( 03 — Contact )
          </p>
          <h2 className="mt-6 font-display text-4xl font-black uppercase leading-[0.95] tracking-tight md:text-6xl">
            Let's get<br />to <span className="text-outline">work</span>
          </h2>
          <p className="mt-8 max-w-md text-base leading-relaxed text-[#A0A0A0]">
            Pricing is per project — by the hour or a full project quote.
            Please call us to get started.
          </p>

          <div className="mt-12 space-y-5">
            <a
              href={PHONE_HREF}
              data-testid="contact-phone-link"
              className="group flex w-fit items-center gap-3 font-display text-2xl font-bold tracking-tight transition-colors duration-300 hover:text-[#0033FF] md:text-3xl"
            >
              {PHONE}
              <ArrowUpRight className="h-5 w-5 text-[#0033FF] transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
            </a>
            <a
              href={`mailto:${EMAIL}`}
              data-testid="contact-email-link"
              className="group flex w-fit items-center gap-3 font-display text-2xl font-bold tracking-tight transition-colors duration-300 hover:text-[#0033FF] md:text-3xl"
            >
              {EMAIL}
              <ArrowUpRight className="h-5 w-5 text-[#0033FF] transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
            </a>
          </div>

          <a
            href={REMOTE_SUPPORT_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-remote-support-btn"
            className="group mt-12 inline-flex items-center gap-3 bg-white px-8 py-4 font-mono text-sm uppercase tracking-[0.2em] text-black transition-colors duration-300 hover:bg-[#0033FF] hover:text-white"
          >
            Remote Connection Support
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
          </a>
        </motion.div>

        <motion.form
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
          className="self-start border border-white/10 bg-[#121212]/70 p-8 backdrop-blur-2xl md:p-10"
          data-testid="contact-form"
        >
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#A0A0A0]">
            Send an enquiry
          </p>
          <div className="mt-8 space-y-5">
            <input
              name="name"
              value={form.name}
              onChange={onChange}
              required
              placeholder="Your name"
              className={inputClass}
              data-testid="contact-name-input"
            />
            <input
              name="email"
              type="email"
              value={form.email}
              onChange={onChange}
              required
              placeholder="Email address"
              className={inputClass}
              data-testid="contact-email-input"
            />
            <textarea
              name="message"
              value={form.message}
              onChange={onChange}
              required
              rows={5}
              placeholder="Tell us about your data or systems challenge"
              className={`${inputClass} resize-none`}
              data-testid="contact-message-input"
            />
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-btn"
              className="group flex w-full items-center justify-center gap-2 bg-[#0033FF] px-8 py-4 font-mono text-sm uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-white hover:text-black disabled:cursor-not-allowed disabled:opacity-60"
            >
              {sending ? (
                <>
                  Sending
                  <Loader2 className="h-4 w-4 animate-spin" />
                </>
              ) : (
                <>
                  Send message
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
                </>
              )}
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
}
